import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, TOKEN_KEY, SESSION_KEY } from "@/lib/api";
import { roleLabel } from "@/lib/format";
import { sessionExpired, markIdleSignOut } from "@/lib/idle";

const AppContext = createContext(null);
const HUB_KEY = "novus_hub";

function readSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    const token = localStorage.getItem(TOKEN_KEY);
    if (!raw || !token) return null;
    // left signed in and unused for 30 minutes (browser closed, laptop asleep): the session has ended
    if (sessionExpired()) { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(SESSION_KEY); markIdleSignOut(); return null; }
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function AppProvider({ children }) {
  const [session, setSession] = useState(readSession);
  // Hub filter for lists/dashboards. "" = all hubs (Super Admin / City Admin);
  // scoped users default to their own hub.
  const [hubId, setHubId] = useState(() => {
    try {
      return localStorage.getItem(HUB_KEY) ?? readSession()?.hubId ?? "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    try {
      if (hubId) localStorage.setItem(HUB_KEY, hubId);
      else localStorage.removeItem(HUB_KEY);
    } catch {}
  }, [hubId]);

  // The axios layer fires this when the API answers 401 (expired token).
  useEffect(() => {
    const onLogout = () => setSession(null);
    window.addEventListener("novus:logout", onLogout);
    return () => window.removeEventListener("novus:logout", onLogout);
  }, []);

  // Re-validate the stored token once on load so a stale session doesn't
  // render a dashboard full of failed requests — and pick up the current
  // permissions and scope for this role (they may have changed since login).
  useEffect(() => {
    if (!session) return;
    api
      .get("/auth/me")
      .then((r) => {
        const u = r.data?.data;
        if (u) setSession((s) => { const next = { ...s, ...pickUser(u) }; try { localStorage.setItem(SESSION_KEY, JSON.stringify(next)); } catch {} return next; });
      })
      .catch(() => {
        /* 401 is handled by the interceptor */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async ({ email, password }) => {
    const res = await api.post("/auth/login", { email, password });
    const { token, user } = res.data.data;
    localStorage.setItem(TOKEN_KEY, token);
    let s = pickUser(user);
    // /auth/me adds the names of the hub / centre / department in scope.
    try { const me = await api.get("/auth/me"); if (me.data?.data) s = { ...s, ...pickUser(me.data.data) }; } catch {}
    localStorage.setItem(SESSION_KEY, JSON.stringify(s));
    setSession(s);
    setHubId(user.hubId || "");
    return s;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(HUB_KEY);
    setSession(null);
  }, []);

  // Mirrors Backend/src/middleware/auth.js: SUPER_ADMIN passes every check,
  // and a role added for the access spec passes wherever one of the legacy
  // roles it borrows from (`aliases`) does.
  const can = useCallback(
    (...roles) => {
      if (!session) return false;
      if (session.role === "SUPER_ADMIN") return true;
      if (roles.length === 0) return false; // authorize() with no roles = Super Admin only
      const mine = [session.role, ...(session.aliases || [])];
      return mine.some((r) => roles.includes(r));
    },
    [session]
  );

  // Permission check against the role matrix: allowed("orders", "CANCEL").
  // Menus and pages use the module alone (any action = the page is yours).
  const allowed = useCallback(
    (module, action) => {
      if (!session) return false;
      if (session.role === "SUPER_ADMIN") return true;
      const p = session.permissions?.[module];
      if (!p || !p.length) return false;
      return action ? p.includes(action) : true;
    },
    [session]
  );

  // Both gates the API applies to an action, in one call: the role matrix
  // (module x action, which a Super Admin edits from Users & Roles) and the
  // route's own role list. Buttons use this — may("orders", "CANCEL",
  // ...CANCEL_ROLES) — so a role sees a button only when the API would let the
  // click through. Without roles the matrix alone decides (routes that only
  // have the module gate, or ones that use permit()).
  const may = useCallback(
    (module, action, ...roles) => allowed(module, action) && (roles.length ? can(...roles) : true),
    [allowed, can]
  );

  // The API's module gate for a write: the role holds some action other than
  // VIEW / PRINT / EXPORT on the module. For a button whose route sits under
  // another page's prefix (receiving money on the Payments page posts to
  // /orders): may("payments", "CREATE", ...) && writes("orders").
  const writes = useCallback(
    (module) => {
      if (!session) return false;
      if (session.role === "SUPER_ADMIN") return true;
      return (session.permissions?.[module] || []).some((a) => !["VIEW", "PRINT", "EXPORT"].includes(a));
    },
    [session]
  );

  const value = useMemo(
    () => ({ session, login, logout, can, allowed, may, writes, hubId, setHubId }),
    [session, login, logout, can, allowed, may, writes, hubId]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

function pickUser(u) {
  return {
    id: u.id,
    name: u.name,
    email: u.email,
    role: u.role,
    roleLabel: u.roleLabel || roleLabel(u.role),
    roleFamily: u.roleFamily || "",
    roleSpec: u.roleSpec || "",
    hubId: u.hubId || null,
    centerId: u.centerId || null,
    cityId: u.cityId || null,
    departmentId: u.departmentId || null,
    zoneId: u.zoneId || null,
    b2bPartnerId: u.b2bPartnerId || null,
    outsourceLabId: u.outsourceLabId || null,
    radiologyPartnerId: u.radiologyPartnerId || null,
    permissions: u.permissions || undefined,
    menu: u.menu || undefined,
    aliases: u.aliases || [],
    scope: u.scope || undefined,
    scopeNames: {
      city: u.city?.name, hub: u.hub?.name, center: u.center?.name, department: u.department?.name,
      zone: u.zone?.name, b2bPartner: u.b2bPartner?.name, outsourceLab: u.outsourceLab?.name, radiologyPartner: u.radiologyPartner?.name,
    },
  };
}

export const useApp = () => useContext(AppContext);
